// Frontend mirror of the backend username moderation (backend/moderation.py).
// The BACKEND is the source of truth — this just gives instant feedback and
// cannot be relied upon for enforcement.

export const USERNAME_GENERIC_REJECT =
  "This username is not allowed. Please choose a different name.";

const MIN = 3;
const MAX = 20;

// Build the invisible/control character class from code points so this source
// file never contains literal invisible characters.
const INVISIBLE_CODEPOINTS = [];
for (let c = 0x200b; c <= 0x200f; c++) INVISIBLE_CODEPOINTS.push(c); // zero-width + marks
for (let c = 0x202a; c <= 0x202e; c++) INVISIBLE_CODEPOINTS.push(c); // bidi embeds/overrides
for (let c = 0x2060; c <= 0x2064; c++) INVISIBLE_CODEPOINTS.push(c); // word joiner..invisible plus
for (let c = 0x2066; c <= 0x206f; c++) INVISIBLE_CODEPOINTS.push(c); // bidi isolates
[0xfeff, 0x00ad, 0x180e].forEach((c) => INVISIBLE_CODEPOINTS.push(c));
const INVISIBLE_CLASS = "[" + INVISIBLE_CODEPOINTS.map((c) => String.fromCharCode(c)).join("") + "]";
const INVISIBLE_RE = new RegExp(INVISIBLE_CLASS);
const INVISIBLE_RE_G = new RegExp(INVISIBLE_CLASS, "g");

const ALLOWED_RE = /^[A-Za-z0-9_-]+$/;

const LEET = {
  0: "o", 1: "i", "!": "i", "|": "i", 3: "e", 4: "a", "@": "a",
  5: "s", $: "s", 7: "t", 8: "b", 9: "g", "+": "t",
};

const BLOCK = [
  "fuck", "shit", "bitch", "bastard", "asshole", "dick", "piss", "cunt",
  "whore", "slut", "wank", "bollocks", "prick", "douche", "dumbass", "jackass",
  "sex", "porn", "penis", "vagina", "boob", "tits", "cum", "rape", "molest",
  "pedo", "horny", "nude", "orgasm", "dildo", "hentai",
  "nigger", "nigga", "faggot", "retard", "spic", "chink", "kike",
  "wetback", "tranny", "dyke", "coon", "gook", "beaner", "paki",
  "nazi", "hitler", "heil", "whitepower", "jihad",
  "killyourself", "kys", "killurself", "neckurself",
];

const collapseRepeats = (s) => s.replace(/(.)\1{2,}/g, "$1$1");

function normalize(name) {
  let s = (name || "").normalize("NFKC").replace(INVISIBLE_RE_G, "").toLowerCase();
  s = s.split("").map((ch) => LEET[ch] || ch).join("");
  s = s.replace(/[^a-z]/g, "");
  return collapseRepeats(s);
}

function containsProfanity(name) {
  const rawLetters = (name || "").toLowerCase().replace(/[^a-z]/g, "");
  const norm = normalize(name);
  return BLOCK.some((b) => norm.includes(b) || rawLetters.includes(b));
}

/**
 * Validate a username. Returns { ok: true, value } or { ok: false, error }.
 * The error for blocked content is intentionally generic.
 */
export function validateUsername(name) {
  if (!name) return { ok: false, error: "Username is required" };
  if (INVISIBLE_RE.test(name)) return { ok: false, error: USERNAME_GENERIC_REJECT };
  const cleaned = name.trim();
  if (cleaned.length < MIN) return { ok: false, error: `Username must be at least ${MIN} characters` };
  if (cleaned.length > MAX) return { ok: false, error: `Username must be ${MAX} characters or fewer` };
  if (!ALLOWED_RE.test(cleaned)) return { ok: false, error: "Username can only contain letters, numbers, underscores and hyphens" };
  if (!/[A-Za-z]/.test(cleaned)) return { ok: false, error: "Username must contain at least one letter" };
  if (/(.)\1{3,}/.test(cleaned)) return { ok: false, error: "Username has too many repeated characters" };
  if (containsProfanity(cleaned)) return { ok: false, error: USERNAME_GENERIC_REJECT };
  return { ok: true, value: cleaned };
}
